/**
 * The client's CDN fallback for art (docs/ops/cdn-static-offload.md §3 lever 1, slice S3).
 *
 * When the page is told to fetch art from the asset CDN (`window.__GC_CONFIG__.assetUrl`, S2), a
 * CDN failure must cost a retry, not a broken zone. So: if a fetch from the CDN fails with a
 * network error or a 5xx, it is retried ONCE from the game's own origin (same path, same `?v=`),
 * and the rest of the session loads every later asset from the origin too ("pinned"). One
 * `console.warn` line records the switch; later failures add nothing.
 *
 * Why only network errors and 5xx: those are the CDN's own faults (measured on its first deploy:
 * two first-hit 500/522 answers that a retry served correctly). A 404 or a parse error is not
 * retried: the deploy gate (S5) only points the page at a CDN whose tree equals the release's, so
 * a file missing there is missing from the origin too.
 *
 * Image loads (three's `TextureLoader`, an `<img>`) report failure as a bare event with no status,
 * so their callers pass `anyError`: any image failure on a CDN URL is retried, and the cost of
 * being wrong is one extra request to the origin.
 *
 * With no CDN configured (`assetUrl` empty, the default) every method is a pass-through.
 */
import { assetHttpBase } from '../../net/platform.js';

/** Is this load error the CDN's fault (network failure, CORS refusal, or a 5xx)? */
export function isRetryableAssetError(err: unknown): boolean {
  // fetch() rejects with a TypeError on a network failure or a CORS refusal.
  if (err instanceof TypeError) return true;
  // three's FileLoader rejects with HttpError, which carries the Response.
  const status =
    (err as { response?: { status?: unknown } } | null)?.response?.status ??
    (err as { status?: unknown } | null)?.status;
  return typeof status === 'number' && status >= 500;
}

export class AssetCdnFallback {
  private readonly cdnBase: string;
  private pinned = false;

  constructor(
    cdnBase: string,
    private readonly warn: (message: string) => void = (message) => console.warn(message),
  ) {
    this.cdnBase = cdnBase.replace(/\/+$/, '');
  }

  /** True once a CDN failure has moved this session's art to the origin. */
  get pinnedToOrigin(): boolean {
    return this.pinned;
  }

  /** Is `url` addressed to the configured CDN? Always false when none is configured. */
  isCdnUrl(url: string): boolean {
    return this.cdnBase !== '' && url.startsWith(`${this.cdnBase}/`);
  }

  /** The same asset on the game's own origin: the CDN prefix dropped (`/assets/...?v=...`). */
  toOrigin(url: string): string {
    return this.isCdnUrl(url) ? url.slice(this.cdnBase.length) : url;
  }

  /** The URL to fetch now: unchanged, or the origin's once the session is pinned. */
  rewrite(url: string): string {
    return this.pinned ? this.toOrigin(url) : url;
  }

  /**
   * After `url` failed with `err`: the origin URL to retry once (and the session is pinned from
   * now on), or null when the failure is not the CDN's to fix. `anyError` is for image loads,
   * whose failure carries no status.
   */
  retryFrom(url: string, err: unknown, anyError = false): string | null {
    if (!this.isCdnUrl(url) || !(anyError || isRetryableAssetError(err))) return null;
    if (!this.pinned) {
      this.pinned = true;
      const why =
        err instanceof Error ? err.message : typeof err === 'string' ? err : 'load error event';
      this.warn(
        `[assets] the asset CDN failed (${why}) on ${url}; this session now loads art from the game's own server`,
      );
    }
    return this.toOrigin(url);
  }

  /** Run a promise-returning load; on a retryable CDN failure, pin and retry once from origin. */
  async load<T>(url: string, attempt: (url: string) => Promise<T>): Promise<T> {
    const first = this.rewrite(url);
    try {
      return await attempt(first);
    } catch (err) {
      const retry = this.retryFrom(first, err);
      if (retry === null) throw err;
      return attempt(retry);
    }
  }
}

let session: AssetCdnFallback | null = null;

/** The session's fallback, created on first use from the page's asset base (`assetHttpBase()`). */
export function sessionAssetCdnFallback(): AssetCdnFallback {
  session ??= new AssetCdnFallback(assetHttpBase());
  return session;
}
